import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Modal, ModalBody, ModalFooter } from './common/Modal';
import { FormField, FormButton } from './common/Form';
import PhoneInput from './common/PhoneInput';
import { Companion, CreateCompanionData } from '../types';
import { companionService } from '../services/companions';
import { useApp } from '../contexts/AppContext';
import { validateDocument, formatDocument } from '../utils/documentValidation';

interface CompanionFormData {
  name: string;
  email: string;
  nationality: string;
  document_type: string;
  document_number: string;
  date_of_birth: string;
  phone: string;
  country_code: string;
}

const companionSchema = yup.object({
  name: yup
    .string()
    .required('Nome é obrigatório')
    .min(2, 'Nome deve ter pelo menos 2 caracteres'),
  email: yup
    .string()
    .email('Digite um email válido')
    .transform((value) => value === '' ? undefined : value)
    .default(''),
  nationality: yup
    .string()
    .required('Nacionalidade é obrigatória'),
  document_type: yup
    .string()
    .required('Tipo de documento é obrigatório'),
  document_number: yup
    .string()
    .test('document-validation', 'Documento inválido', function(value) {
      const { document_type } = this.parent;
      if (!value || !document_type) return false;
      return validateDocument(value, document_type);
    })
    .required('Número do documento é obrigatório'),
  date_of_birth: yup
    .string()
    .required('Data de nascimento é obrigatória')
    .matches(/^\d{4}-\d{2}-\d{2}$/, 'Data deve estar no formato YYYY-MM-DD'),
  phone: yup
    .string()
    .matches(/^\d{9,11}$/, 'Telefone deve conter entre 9 e 11 dígitos')
    .transform((value) => value === '' ? undefined : value)
    .default(''),
  country_code: yup
    .string()
    .default('+55'),
});

interface CompanionModalProps {
  isOpen: boolean;
  onClose: () => void;
  companion?: Companion | null;
  mode: 'create' | 'edit';
}

export function CompanionModal({ isOpen, onClose, companion, mode }: CompanionModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addCompanion, updateCompanion, refreshCompanions, companions } = useApp();

  // Format date for input type="date" (YYYY-MM-DD)
  const formatDateForInput = (dateStr?: string): string => {
    if (!dateStr) return '';
    
    // If already in YYYY-MM-DD format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }
    
    try {
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useForm<CompanionFormData>({
    resolver: yupResolver(companionSchema),
    defaultValues: {
      nationality: 'BR',
      document_type: 'CPF'
    }
  });

  const watchedNationality = watch('nationality');
  const watchedDocumentType = watch('document_type');

  // Reset form when modal opens/closes or companion changes
  useEffect(() => {
    if (isOpen) {
      if (mode === 'edit' && companion) {
        // Populate form with companion data
        setValue('name', companion.name);
        setValue('email', companion.email || '');
        setValue('nationality', companion.nationality || 'BR');
        setValue('document_type', companion.document_type || 'CPF');
        setValue('document_number', companion.document_number);
        setValue('date_of_birth', formatDateForInput(companion.date_of_birth));
        setValue('phone', companion.phone || '');
        setValue('country_code', companion.country_code || '+55');
      } else {
        // Reset form for create mode
        reset({
          nationality: 'BR',
          document_type: 'CPF',
          country_code: '+55'
        });
      }
      setError(null);
    }
  }, [isOpen, mode, companion, setValue, reset]);

  const onSubmit = async (data: CompanionFormData) => {
    try {
      setIsLoading(true);
      setError(null);

      console.log('📝 CompanionModal: Submitting data:', { mode, data });

      // Check for duplicate document number (only in create mode or if document changed)
      if (mode === 'create' || (companion && companion.document_number !== data.document_number)) {
        const cleanDocNumber = data.document_number.replace(/\D/g, '');
        const existingCompanion = companions.find(c => 
          c.id !== companion?.id && 
          c.document_number.replace(/\D/g, '') === cleanDocNumber
        );
        
        if (existingCompanion) {
          setError('Este número de documento já está cadastrado para outro acompanhante');
          return;
        }
      }

      const companionData: CreateCompanionData = {
        name: data.name,
        email: data.email || null,
        nationality: data.nationality,
        document_type: data.document_type,
        document_number: data.document_number,
        date_of_birth: data.date_of_birth,
        phone: data.phone || null,
        country_code: data.country_code || '+55',
      };

      if (mode === 'create') {
        const response = await companionService.createCompanion(companionData);
        console.log('✅ CompanionModal: Companion created:', response);
        
        // Handle both direct companion object and wrapped response
        const companionToAdd = (response as any).companion || response;
        addCompanion(companionToAdd);
      } else if (mode === 'edit' && companion) {
        const response = await companionService.updateCompanion(companion.id, companionData);
        console.log('✅ CompanionModal: Companion updated:', response);
        
        // Handle both direct companion object and wrapped response
        const companionToUpdate = (response as any).companion || response;
        updateCompanion(companionToUpdate);
      }

      // Refresh companions list
      await refreshCompanions();
      
      // Close modal and reset form
      onClose();
      reset();
    } catch (err: any) {
      let errorMessage = 'Erro ao salvar acompanhante';
      
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      // Handle specific validation errors
      if (err.response?.status === 422) {
        if (errorMessage.includes('document number has already been taken') || 
            errorMessage.includes('document_number has already been taken')) {
          errorMessage = 'Este número de documento já está cadastrado';
        } else if (errorMessage.includes('email has already been taken')) {
          errorMessage = 'Este email já está cadastrado';
        }
        
        // Handle detailed validation errors
        if (err.response?.data?.errors) {
          const errors = err.response.data.errors;
          const errorMessages = [];
          
          if (errors.document_number) {
            errorMessages.push('Número do documento: ' + errors.document_number.join(', '));
          }
          if (errors.email) {
            errorMessages.push('Email: ' + errors.email.join(', '));
          }
          if (errors.name) {
            errorMessages.push('Nome: ' + errors.name.join(', '));
          }
          
          if (errorMessages.length > 0) {
            errorMessage = errorMessages.join('\n');
          }
        }
      }
      
      console.error('❌ CompanionModal: Error:', errorMessage, err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
      reset();
      setError(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Adicionar Acompanhante' : 'Editar Acompanhante'}
      size="md"
      closable={!isLoading}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody>
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="text-red-600 text-sm">
                  {error.split('\n').map((line, index) => (
                    <div key={index} className={index > 0 ? 'mt-1' : ''}>
                      {line}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* Name */}
            <FormField
              label="Nome Completo"
              name="name"
              type="text"
              placeholder="Digite o nome completo"
              register={register}
              error={errors.name}
              required
              disabled={isLoading}
              autoComplete="name"
            />

            {/* Email */}
            <FormField
              label="Email"
              name="email"
              type="email"
              placeholder="email@exemplo.com (opcional)"
              register={register}
              error={errors.email}
              disabled={isLoading}
              autoComplete="email"
            />

            {/* Nationality */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nacionalidade
              </label>
              <select
                {...register('nationality')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                disabled={isLoading}
              >
                <option value="">Selecione a nacionalidade</option>
                <option value="BR">🇧🇷 Brasil</option>
                <option value="US">🇺🇸 Estados Unidos</option>
                <option value="GB">🇬🇧 Reino Unido</option>
                <option value="FR">🇫🇷 França</option>
                <option value="DE">🇩🇪 Alemanha</option>
                <option value="ES">🇪🇸 Espanha</option>
                <option value="IT">🇮🇹 Itália</option>
                <option value="PT">🇵🇹 Portugal</option>
                <option value="AR">🇦🇷 Argentina</option>
                <option value="CL">🇨🇱 Chile</option>
                <option value="CO">🇨🇴 Colômbia</option>
                <option value="PE">🇵🇪 Peru</option>
                <option value="UY">🇺🇾 Uruguai</option>
                <option value="PY">🇵🇾 Paraguai</option>
                <option value="BO">🇧🇴 Bolívia</option>
                <option value="EC">🇪🇨 Equador</option>
                <option value="VE">🇻🇪 Venezuela</option>
                <option value="CA">🇨🇦 Canadá</option>
                <option value="MX">🇲🇽 México</option>
                <option value="JP">🇯🇵 Japão</option>
                <option value="CN">🇨🇳 China</option>
                <option value="IN">🇮🇳 Índia</option>
                <option value="AU">🇦🇺 Austrália</option>
                <option value="OTHER">🌐 Outro</option>
              </select>
              {errors.nationality && (
                <p className="mt-1 text-sm text-red-600">{errors.nationality.message}</p>
              )}
            </div>

            {/* Document Type and Number */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Documento
                </label>
                <select
                  {...register('document_type')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  disabled={isLoading}
                >
                  <option value="CPF">CPF</option>
                  <option value="Passaporte">Passaporte</option>
                  <option value="Outro">Outro</option>
                </select>
                {errors.document_type && (
                  <p className="mt-1 text-sm text-red-600">{errors.document_type.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {watchedDocumentType === 'CPF' ? 'CPF' : 'Número do Documento'}
                </label>
                <input
                  {...register('document_number')}
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                  placeholder={watchedDocumentType === 'CPF' ? '000.000.000-00' : 'Digite o número do documento'}
                  disabled={isLoading}
                  onChange={(e) => {
                    const formatted = formatDocument(e.target.value, watchedDocumentType);
                    e.target.value = formatted;
                    register('document_number').onChange(e);
                  }}
                />
                {errors.document_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.document_number.message}</p>
                )}
              </div>
            </div>

            {/* Birth Date */}
            <FormField
              label="Data de Nascimento"
              name="date_of_birth"
              type="date"
              register={register}
              error={errors.date_of_birth}
              required
              disabled={isLoading}
            />

            {/* Phone with Country Code */}
            <PhoneInput
              label="Telefone"
              phoneRegister={register('phone')}
              countryCodeRegister={register('country_code')}
              phoneError={errors.phone}
              countryCodeError={errors.country_code}
              disabled={isLoading}
              placeholder="(85) 99999-9999"
            />
          </div>

          {/* Help Text */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>💡 Dica:</strong> Salve seus acompanhantes frequentes para facilitar futuras viagens. 
              Os dados serão usados para preencher automaticamente os formulários de viagem.
            </p>
          </div>
        </ModalBody>

        <ModalFooter>
          <div className="flex justify-end space-x-3">
            <FormButton
              type="button"
              variant="ghost"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancelar
            </FormButton>
            <FormButton
              type="submit"
              variant="primary"
              loading={isLoading}
              disabled={isLoading}
            >
              {mode === 'create' ? 'Adicionar' : 'Salvar'}
            </FormButton>
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}

export default CompanionModal;
